import { Container, Group, Title, Text, SimpleGrid, Card, Table, Skeleton } from '@mantine/core';
import { useSchoolSalesAnalysis, useSchoolSalesByCategory } from '@/hooks/usePortalApi';

export default function SalesAnalysis() {
  const { data: analysis, isLoading: analysisLoading } = useSchoolSalesAnalysis();
  const { data: categoryData, isLoading: categoryLoading } = useSchoolSalesByCategory();

  const fmt = (n: number) => '$' + n.toFixed(2);

  return (
    <Container size="lg" py="md">
      <Group justify="space-between" mb="md">
        <div>
          <Title order={2}>School Sales Analysis</Title>
          <Text c="dimmed" size="sm">Comprehensive sales and order analytics</Text>
        </div>
      </Group>

      {analysisLoading ? (
        <SimpleGrid cols={4} mb="xl">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i} shadow="sm" padding="lg" withBorder>
              <Skeleton height={20} width="60%" mb="xs" />
              <Skeleton height={30} width="40%" />
            </Card>
          ))}
        </SimpleGrid>
      ) : analysis ? (
        <SimpleGrid cols={4} mb="xl">
          <Card shadow="sm" padding="lg" withBorder>
            <Text size="sm" c="dimmed" mb="xs">Total Sales</Text>
            <Text size="xl" fw={700}>{fmt(analysis.total_sales)}</Text>
          </Card>

          <Card shadow="sm" padding="lg" withBorder>
            <Text size="sm" c="dimmed" mb="xs">Total Orders</Text>
            <Text size="xl" fw={700}>{analysis.total_orders}</Text>
          </Card>

          <Card shadow="sm" padding="lg" withBorder>
            <Text size="sm" c="dimmed" mb="xs">Avg Order Value</Text>
            <Text size="xl" fw={700}>{fmt(analysis.avg_order_value)}</Text>
          </Card>

          <Card shadow="sm" padding="lg" withBorder>
            <Text size="sm" c="dimmed" mb="xs">Top Category</Text>
            <Text size="xl" fw={700}>{analysis.top_category}</Text>
          </Card>
        </SimpleGrid>
      ) : null}

      <Card shadow="sm" padding="lg" withBorder mb="xl">
        <Title order={4} mb="md">Monthly Breakdown</Title>
        {analysisLoading ? (
          <div>
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} height={40} mb="sm" />
            ))}
          </div>
        ) : !analysis?.monthly || analysis.monthly.length === 0 ? (
          <Text ta="center" c="dimmed" py="md">No monthly data available</Text>
        ) : (
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Month</Table.Th>
                <Table.Th>Orders</Table.Th>
                <Table.Th>Revenue</Table.Th>
                <Table.Th>Avg Order</Table.Th>
                <Table.Th>Top Products</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {analysis.monthly.map((month: any) => (
                <Table.Tr key={month.month}>
                  <Table.Td>{month.month}</Table.Td>
                  <Table.Td>{month.orders}</Table.Td>
                  <Table.Td>{fmt(month.revenue)}</Table.Td>
                  <Table.Td>{fmt(month.avg_order)}</Table.Td>
                  <Table.Td>{month.top_products}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Card>

      <Card shadow="sm" padding="lg" withBorder>
        <Title order={4} mb="md">Category Breakdown</Title>
        {categoryLoading ? (
          <SimpleGrid cols={3}>
            {[1, 2, 3].map((i) => (
              <Card key={i} shadow="sm" padding="md" withBorder>
                <Skeleton height={20} width="60%" mb="xs" />
                <Skeleton height={30} width="40%" />
              </Card>
            ))}
          </SimpleGrid>
        ) : !categoryData || categoryData.length === 0 ? (
          <Text ta="center" c="dimmed" py="md">No category data available</Text>
        ) : (
          <SimpleGrid cols={3}>
            {categoryData.map((category: any) => (
              <Card key={category.name} shadow="sm" padding="md" withBorder>
                <Text size="sm" c="dimmed" mb="xs">{category.name}</Text>
                <Text size="lg" fw={700}>{fmt(category.amount)}</Text>
                <Text size="xs" c="dimmed">{category.percentage}% of total</Text>
              </Card>
            ))}
          </SimpleGrid>
        )}
      </Card>
    </Container>
  );
}
